/**
 * TimelineTaskLayer Component
 *
 * Renders task rectangles for all crops.
 * Position: Below phase rects when both shown, otherwise at top of row.
 */

import React, { useMemo, useCallback } from 'react'
import { Typography, darken, useTheme } from '@mui/material'
import {
  useTimelineContextDispatch,
  setExpandedIds as setExpandedIdsAction,
  setHoveredTaskKey as setHoveredTaskKeyAction,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'
import { PannableRect } from '../content/pannable-rect'
import { TimelinePopsicleBadge, POPSICLE_BADGE_R } from '../content/timeline-popsicle-badge'

// Stable sx object — defined once at module level, never reallocated
const ICON_SX = {
  fontSize: 14,
  lineHeight: 1,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

const REMOVE_TASK_ICON_RIGHT_OFFSET = 6
const REMOVE_TASK_ICON_TOP_OFFSET = 2

/**
 * TimelineTaskLayer - Task rectangles for all crops
 *
 * Renders task bars with icons. Click to expand/collapse in non-interactive mode.
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data with week-based tasks
 * @param {boolean} props.interactive - Enable interactive mode (disables click to expand)
 */
export const TimelineTaskLayer = React.memo(({ crops, interactive, onPanStart, panningTaskIndex, isTaskPanning, isSchedulePanning, onRemoveTask, hideBadges = false }) => {
  const {
    expandedIds,
    weekWidth,
    getRowY,
    forceAllExpand,
    viewStrategy,
    NOW_X,
    collapsedRowHeight,
    collapsedRowHeightGap,
  } = useTimelineStaticContext()
  const { hoveredTaskKey } = useTimelineInteractionContext()

  const dispatch = useTimelineContextDispatch()
  const { hoveredOrg, hoveredTask } = useOrgHoverContext()
  const { getTaskStyle, PHASE_BORDER_COLOR } = useTimelineStyles()
  const theme = useTheme()

  const BADGE_R = POPSICLE_BADGE_R

  // Stable delegated hover handlers — one pair per component, not per rect
  const handleMouseOver = useCallback((e) => {
    const taskKey = e.target.dataset.taskKey ?? null
    dispatch(setHoveredTaskKeyAction({ hoveredTaskKey: taskKey }))
  }, [dispatch])
  const handleMouseLeave = useCallback(
    () => dispatch(setHoveredTaskKeyAction({ hoveredTaskKey: null })),
    [dispatch]
  )

  // Sorted task indices per crop — only re-computes when task data or hover state changes.
  // readOnly ordering is stable; hover ordering changes only on task mouseenter/leave.
  const sortedTaskIndices = useMemo(() => {
    const map = new Map()
    crops.forEach(crop => {
      if (!crop.tasks?.length) return
      const indices = crop.tasks.map((_, i) => i)
      indices.sort((a, b) => {
        const taskA = crop.tasks[a]
        const taskB = crop.tasks[b]
        // Non-readOnly (preview) always renders on top of readOnly (existing) — highest priority
        if (!taskA.readOnly && taskB.readOnly) return 1
        if (taskA.readOnly && !taskB.readOnly) return -1
        // Within same readOnly group, render hovered task on top
        if (taskA.id === hoveredTaskKey || taskA.id === hoveredTask) return 1
        if (taskB.id === hoveredTaskKey || taskB.id === hoveredTask) return -1
        return 0
      })
      map.set(crop.id, indices)
    })
    return map
  }, [crops, hoveredTaskKey, hoveredTask])

  return (
    <>
      {crops.map((crop) => {
        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = isExpanded
          ? EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP
          : collapsedRowHeight - collapsedRowHeightGap

        // Calculate sub-row height when both phases and tasks shown
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH

        // Position in bottom half when both, otherwise center in full row
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + subRowH + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        return (
          // Delegated hover: onMouseOver reads data-task-key from the target rect,
          // onMouseLeave fires once when the mouse exits the entire crop row.
          <g key={`${crop.id}-tasks`} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
            {crop.tasks?.length > 0 && (sortedTaskIndices.get(crop.id) ?? []).map((tidx) => {
              const task = crop.tasks[tidx]
              const s = getTaskStyle(task.type)
              const x = task.start * weekWidth
              const taskWidth = task.len * weekWidth
              const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
              const isTaskHovered = task.id === hoveredTask
              const taskKey = task.id

              const fillColor = isParentOrgHovered || isTaskHovered ? darken(s.color, 0.2) : s.color
              const strokeW = isParentOrgHovered || isTaskHovered ? 1 : 0.25
              const isCompleted = task.state === 'completed'
              const completedAtWeeks = task.completed_at_weeks ?? null
              const readOnly = task.readOnly ?? false
              const isMultiSubmit = task.single_completion === false

              // Completion badge cx — computed inline, no IIFE allocation
              const today = isCompleted && completedAtWeeks != null ? new Date().toDateString() : null
              const completedDay = today && task.completed_at ? new Date(task.completed_at).toDateString() : null
              const badgeCx = today != null
                ? Math.max(x + BADGE_R + 2, Math.min(x + taskWidth - BADGE_R - 2,
                    completedDay === today && NOW_X != null ? NOW_X : completedAtWeeks * weekWidth))
                : null

              return (
                <g key={tidx}>
                  {((interactive || crop.interactive) && !readOnly && !isCompleted) ? (
                    /* Interactive mode: individual task panning enabled, expand/collapse disabled */
                    <PannableRect
                      data-task-key={taskKey}
                      x={x}
                      y={y}
                      width={taskWidth}
                      height={barH}
                      fill={fillColor}
                      rx={6}
                      stroke={PHASE_BORDER_COLOR}
                      strokeWidth={strokeW}
                      isPanning={(isTaskPanning && panningTaskIndex === tidx) || isSchedulePanning}
                      onPanStart={(clientX) => onPanStart(tidx, clientX)}
                    >
                      {isMultiSubmit && (
                        <rect
                          x={x + 2}
                          y={y + 2}
                          width={taskWidth - 4}
                          height={barH - 4}
                          fill="none"
                          rx={4}
                          stroke="white"
                          strokeWidth={1}
                          style={{ pointerEvents: 'none' }}
                        />
                      )}
                      {/* Task icon */}
                      {!isMultiSubmit && (
                        <foreignObject
                          x={x + 5}
                          y={isExpanded ? y + 6 : y + (barH / 2) - (16 / 2)}
                          width={isExpanded ? 20 : 16}
                          height={isExpanded ? 20 : 16}
                          style={{ pointerEvents: 'none' }}
                        >
                          <Typography sx={ICON_SX}>{s.icon}</Typography>
                        </foreignObject>
                      )}
                    </PannableRect>
                  ) : (
                    /* Non-interactive / read-only mode */
                    <g
                      style={{ cursor: readOnly ? 'default' : 'pointer' }}
                      onClick={readOnly ? undefined : (e) => {
                        e.stopPropagation()
                        const newExpandedIds = expandedIds.includes(crop.id)
                          ? expandedIds.filter(i => i !== crop.id)
                          : [...expandedIds, crop.id]
                        dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
                      }}
                    >
                      {/* Task rectangle */}
                      <rect
                        data-task-key={taskKey}
                        x={x}
                        y={y}
                        width={taskWidth}
                        height={barH}
                        fill={fillColor}
                        rx={6}
                        stroke={PHASE_BORDER_COLOR}
                        strokeWidth={strokeW}
                        style={{ filter: 'drop-shadow(0 0 0.5px #1976d2), drop-shadow(0 0 2.5px #1976d2)' }}
                      />
                      {isMultiSubmit && (
                        <rect
                          x={x + 3}
                          y={y + 3}
                          width={taskWidth - 6}
                          height={barH - 6}
                          fill="none"
                          rx={4}
                          stroke="white"
                          strokeWidth={1.5}
                          style={{ pointerEvents: 'none' }}
                        />
                      )}

                      {/* Task icon */}
                      {!isMultiSubmit && (
                        <foreignObject
                          x={x + 5}
                          y={isExpanded ? y + 6 : y + (barH / 2) - (16 / 2)}
                          width={isExpanded ? 20 : 16}
                          height={isExpanded ? 20 : 16}
                          style={{ pointerEvents: 'none' }}
                        >
                          <Typography sx={ICON_SX}>{s.icon}</Typography>
                        </foreignObject>
                      )}
                    </g>
                  )}

                  {/* Completion popsicle badge */}
                  {badgeCx != null && !hideBadges && (
                    <TimelinePopsicleBadge
                      cx={badgeCx}
                      y={y}
                      color={theme.palette.success.main}
                    />
                  )}

                  {/* Remove task badge — shown on hover during edit mode */}
                  {onRemoveTask && crop.interactive && !readOnly && !isCompleted && !hideBadges && (
                    <TimelinePopsicleBadge
                      cx={x + taskWidth - BADGE_R - REMOVE_TASK_ICON_RIGHT_OFFSET}
                      y={y + REMOVE_TASK_ICON_TOP_OFFSET}
                      color={theme.palette.error.main}
                      icon="𐌗"
                      onClick={() => onRemoveTask(task.id)}
                    />
                  )}
                </g>
              )
            })}
          </g>
        )
      })}
    </>
  )
})
